import React, { useEffect, useRef, useState } from "react";
import GalleryModal from "./GalleryModal";
import ProgressiveImage from "./ProgressiveImage";

function PortfolioSection({ title, artworks, printCatalog }) {
  const scrollContainerRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollState, setScrollState] = useState({ canPrev: false, canNext: false });

  const getOrientationClass = (image) => {
    if (!image?.width || !image?.height) {
      return 'portfolio-item--square';
    }

    const ratio = image.width / image.height;

    if (ratio >= 1.25) {
      return 'portfolio-item--landscape';
    }

    if (ratio <= 0.75) {
      return 'portfolio-item--portrait';
    }

    return 'portfolio-item--square';
  };

  const updateScrollState = () => {
    const container = scrollContainerRef.current;
    if (!container) {
      setScrollState({ canPrev: false, canNext: false });
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScrollLeft = scrollWidth - clientWidth;
    const buffer = 1; // allow for fractional rounding

    setScrollState({
      canPrev: scrollLeft > buffer,
      canNext: scrollLeft < maxScrollLeft - buffer,
    });
  };

  const scrollByAmount = (direction = 1) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const scrollAmount = container.clientWidth * 0.9 * direction;
    container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    // allow smooth scroll to update state via listener, but trigger a check immediately
    requestAnimationFrame(updateScrollState);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [artworks]);

  const handleOpenModal = (index) => {
    setActiveIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  if (!artworks?.length) return null;

  return (
    <section className="portfolio-category full-height-gallery">
      <div className="portfolio-category-title-container">
        <h2 className="portfolio-category-title">{title}</h2>
      </div>

      <div className="portfolio-scroll-wrapper">
        {scrollState.canPrev && (
          <button
            type="button"
            className="scroll-btn scroll-btn-left"
            onClick={() => scrollByAmount(-1)}
            aria-label="Scroll gallery left"
          >
            ‹
          </button>
        )}

        <div className="portfolio-scroll" ref={scrollContainerRef}>
          <div className="portfolio-row">
            {artworks.map((artwork, index) => (
              <div 
                key={artwork._id} 
                className={`portfolio-item ${getOrientationClass(artwork.image)}`}
                onClick={() => handleOpenModal(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOpenModal(index);
                  }
                }}
              >
                <ProgressiveImage
                  src={artwork.image?.src}
                  placeholder={artwork.image?.preview?.src}
                  alt={artwork.title}
                  className="portfolio-image"
                  wrapperClassName="portfolio-image-wrapper"
                  loading="lazy"
                />
                <div className="portfolio-overlay">
                  <h3 className="portfolio-title">{artwork.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>

        {scrollState.canNext && (
          <button
            type="button"
            className="scroll-btn scroll-btn-right"
            onClick={() => scrollByAmount(1)}
            aria-label="Scroll gallery right"
          >
            ›
          </button>
        )}
      </div>

      <GalleryModal
        items={artworks}
        isOpen={isModalOpen}
        initialIndex={activeIndex}
        onClose={handleCloseModal}
        printCatalog={printCatalog}
      />
    </section>
  );
}

export default PortfolioSection;
